import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Trophy, Star, Medal, Award, Target, Users, Crown,
  Loader2, RefreshCw, Zap, Heart, Clock, TrendingUp,
  Megaphone, Send, Settings, Plus, X, Check, User
} from 'lucide-react';
import { COLORS } from '../../../constants/dashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GamificationView = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('leaderboard');
  const [leaderboard, setLeaderboard] = useState([]);
  const [allBadges, setAllBadges] = useState([]);
  const [shoutouts, setShoutouts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [shoutoutSettings, setShoutoutSettings] = useState({
    auto_volunteer_hours: 50,
    auto_donation_amount: 500,
    auto_enabled: true
  });
  
  // Shoutout modal state
  const [showShoutoutModal, setShowShoutoutModal] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [shoutoutForm, setShoutoutForm] = useState({
    type: 'volunteering',
    message: '',
    is_public: true
  });
  const [submitting, setSubmitting] = useState(false);

  const isAdmin = user?.role === 'super_admin' || user?.role === 'business_admin';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [leaderboardRes, allBadgesRes, shoutoutsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/gamification/leaderboard`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/badges`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/shoutouts`, { withCredentials: true }).catch(() => ({ data: { shoutouts: [] } }))
      ]);
      setLeaderboard(leaderboardRes.data.leaderboard || []);
      setAllBadges(allBadgesRes.data.badges || []);
      setShoutouts(shoutoutsRes.data.shoutouts || []);
      
      // Fetch employees for shoutout modal
      if (isAdmin) {
        const usersRes = await axios.get(`${BACKEND_URL}/api/users`, { withCredentials: true });
        const usersList = usersRes.data.users || usersRes.data || [];
        setEmployees(usersList.filter(u => u.role === 'employee') || []);
        
        // Fetch shoutout settings
        const settingsRes = await axios.get(`${BACKEND_URL}/api/shoutouts/settings`, { withCredentials: true }).catch(() => ({ data: {} }));
        if (settingsRes.data) setShoutoutSettings(prev => ({ ...prev, ...settingsRes.data }));
      }
    } catch (err) {
      console.error('Failed to fetch gamification data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-400" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="w-5 h-5 flex items-center justify-center text-sm font-bold text-gray-500">#{rank}</span>;
  };

  const handleCreateShoutout = async () => {
    if (selectedEmployees.length === 0) {
      alert('Please select at least one employee');
      return;
    }
    if (!shoutoutForm.message.trim()) {
      alert('Please enter a message');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/shoutouts`, {
        employee_ids: selectedEmployees,
        type: shoutoutForm.type,
        message: shoutoutForm.message,
        is_public: shoutoutForm.is_public
      }, { withCredentials: true });
      
      setShowShoutoutModal(false);
      setSelectedEmployees([]);
      setShoutoutForm({ type: 'volunteering', message: '', is_public: true });
      fetchData();
      alert('Shoutout sent successfully!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create shoutout');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/shoutouts/settings`, shoutoutSettings, { withCredentials: true });
      alert('Settings saved successfully!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save settings');
    }
  };

  const deleteShoutout = async (shoutoutId) => {
    if (!window.confirm('Delete this shoutout?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/shoutouts/${shoutoutId}`, { withCredentials: true });
      fetchData();
    } catch (err) {
      alert('Failed to delete shoutout');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-4" style={{ background: COLORS.background }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold" style={{ color: COLORS.primary }}>Gamification & Recognition</h2>
          <p className="text-sm text-gray-500">Manage leaderboards, badges, and employee recognition</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button 
              onClick={() => setShowShoutoutModal(true)}
              className="px-4 py-2 text-white rounded-lg flex items-center gap-2"
              style={{ background: COLORS.primary }}
            >
              <Megaphone className="w-4 h-4" />
              Give Shoutout
            </button>
          )}
          <button onClick={fetchData} className="p-2 bg-white rounded-lg border shadow-sm hover:bg-gray-50">
            <RefreshCw className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl" style={{ background: `${COLORS.primary}15` }}>
              <Users className="w-6 h-6" style={{ color: COLORS.primary }} />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{ color: COLORS.primary }}>{leaderboard.length}</p>
              <p className="text-xs text-gray-500">Active Participants</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl" style={{ background: `${COLORS.success}15` }}>
              <Award className="w-6 h-6" style={{ color: COLORS.success }} />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{ color: COLORS.success }}>{allBadges.length}</p>
              <p className="text-xs text-gray-500">Available Badges</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl" style={{ background: `${COLORS.warning}15` }}>
              <Megaphone className="w-6 h-6" style={{ color: COLORS.warning }} />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{ color: COLORS.warning }}>{shoutouts.length}</p>
              <p className="text-xs text-gray-500">Shoutouts Given</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl" style={{ background: `${COLORS.accent}15` }}>
              <TrendingUp className="w-6 h-6" style={{ color: COLORS.accent }} />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{ color: COLORS.accent }}>
                {leaderboard.reduce((sum, l) => sum + (l.points || 0), 0).toLocaleString()}
              </p>
              <p className="text-xs text-gray-500">Total Points Earned</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        {[
          { id: 'leaderboard', label: 'Leaderboard', icon: Trophy },
          { id: 'shoutouts', label: 'Employee Shoutouts', icon: Megaphone },
          { id: 'badges', label: 'Badge Catalog', icon: Award },
          ...(isAdmin ? [{ id: 'settings', label: 'Auto-Shoutout Settings', icon: Settings }] : [])
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-2 transition-all ${
              activeTab === tab.id
                ? 'text-white shadow-md'
                : 'bg-white text-gray-600 border hover:bg-gray-50'
            }`}
            style={activeTab === tab.id ? { background: COLORS.primary } : {}}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Leaderboard Tab */}
      {activeTab === 'leaderboard' && (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="p-4 border-b" style={{ background: `${COLORS.primary}05` }}>
            <h3 className="font-bold" style={{ color: COLORS.primary }}>Company Leaderboard</h3>
            <p className="text-xs text-gray-500">Top contributors ranked by total points</p>
          </div>
          {leaderboard.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Trophy className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No leaderboard data yet. Employees will appear here as they contribute!</p>
            </div>
          ) : (
            <div className="divide-y">
              {leaderboard.map((entry, idx) => (
                <div key={entry.user_id} className="flex items-center justify-between p-4 hover:bg-gray-50">
                  <div className="flex items-center gap-4">
                    {getRankIcon(entry.rank)}
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" 
                      style={{ background: idx < 3 ? COLORS.primary : COLORS.muted }}>
                      {(entry.name || 'A')[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{entry.name || 'Anonymous'}</p>
                      <p className="text-xs text-gray-500">{entry.department || 'Team Member'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <p className="font-bold text-gray-900">${entry.total_donated?.toLocaleString() || 0}</p>
                      <p className="text-xs text-gray-500">Donated</p>
                    </div>
                    <div className="text-center">
                      <p className="font-bold text-gray-900">{entry.volunteer_hours || 0}h</p>
                      <p className="text-xs text-gray-500">Volunteered</p>
                    </div>
                    <div className="text-center">
                      <p className="font-bold" style={{ color: COLORS.primary }}>{entry.points || 0}</p>
                      <p className="text-xs text-gray-500">Points</p>
                    </div>
                    <div className="flex gap-1">
                      {entry.top_badges?.slice(0, 3).map((badge, i) => (
                        <div key={i} className="w-8 h-8 rounded-full flex items-center justify-center text-lg" 
                          style={{ background: `${COLORS.warning}20` }} title={badge.name}>
                          {badge.icon || '🏆'}
                        </div>
                      ))}
                    </div>
                    {isAdmin && (
                      <button
                        onClick={() => {
                          setSelectedEmployees([entry.user_id]);
                          setShowShoutoutModal(true);
                        }}
                        className="p-2 hover:bg-blue-50 rounded-lg"
                        title="Give shoutout"
                      >
                        <Megaphone className="w-4 h-4 text-gray-400 hover:text-blue-600" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Shoutouts Tab */}
      {activeTab === 'shoutouts' && (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="p-4 border-b" style={{ background: `${COLORS.warning}10` }}>
            <h3 className="font-bold" style={{ color: COLORS.warning }}>Employee Shoutouts</h3>
            <p className="text-xs text-gray-500">Recognition for outstanding contributions</p>
          </div>
          {shoutouts.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Megaphone className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No shoutouts yet. Recognize your employees amazing work!</p>
              {isAdmin && (
                <button 
                  onClick={() => setShowShoutoutModal(true)}
                  className="mt-4 px-4 py-2 text-white rounded-lg"
                  style={{ background: COLORS.primary }}
                >
                  Give First Shoutout
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y">
              {shoutouts.map(shoutout => (
                <div key={shoutout.shoutout_id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-full" style={{ background: `${COLORS.warning}20` }}>
                        {shoutout.type === 'volunteering' ? (
                          <Clock className="w-5 h-5" style={{ color: COLORS.warning }} />
                        ) : shoutout.type === 'donation' ? (
                          <Heart className="w-5 h-5 text-red-500" />
                        ) : (
                          <Star className="w-5 h-5" style={{ color: COLORS.warning }} />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          {shoutout.employees?.map((emp, i) => (
                            <span key={i} className="font-semibold text-gray-900">{emp.name || emp.email}</span>
                          ))}
                          {shoutout.is_public && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">Public</span>
                          )}
                        </div>
                        <p className="text-gray-600">{shoutout.message}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {shoutout.type === 'volunteering' ? '🤝 Volunteering' : shoutout.type === 'donation' ? '💝 Donation' : '⭐ Milestone'}
                          {' • '}{new Date(shoutout.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    {isAdmin && (
                      <button
                        onClick={() => deleteShoutout(shoutout.shoutout_id)}
                        className="p-1.5 hover:bg-red-50 rounded text-gray-400 hover:text-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Badges Tab */}
      {activeTab === 'badges' && (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="p-4 border-b" style={{ background: `${COLORS.primary}05` }}>
            <h3 className="font-bold" style={{ color: COLORS.primary }}>Badge Catalog</h3>
            <p className="text-xs text-gray-500">All badges employees can earn through their contributions</p>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-4 gap-4">
              {allBadges.map(badge => (
                <div key={badge.badge_id} className="p-4 rounded-xl border bg-white hover:shadow-md transition-all text-center">
                  <div className="w-16 h-16 mx-auto mb-2 rounded-full flex items-center justify-center text-3xl"
                    style={{ background: `${COLORS.primary}15` }}>
                    {badge.icon || '🏆'}
                  </div>
                  <p className="font-bold text-gray-900">{badge.name}</p>
                  <p className="text-xs text-gray-500 mb-2">{badge.description}</p>
                  <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ background: `${COLORS.success}20`, color: COLORS.success }}>
                    +{badge.points} pts
                  </span>
                  <p className="text-xs text-gray-400 mt-2 capitalize">{badge.category}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Settings Tab (Admin Only) */}
      {activeTab === 'settings' && isAdmin && (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="p-4 border-b" style={{ background: `${COLORS.accent}10` }}>
            <h3 className="font-bold" style={{ color: COLORS.accent }}>Auto-Shoutout Configuration</h3>
            <p className="text-xs text-gray-500">Configure automatic recognition for employee milestones</p>
          </div>
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Enable Auto-Shoutouts</p>
                <p className="text-sm text-gray-500">Automatically recognize employees when they hit milestones</p>
              </div>
              <button
                onClick={() => setShoutoutSettings(prev => ({ ...prev, auto_enabled: !prev.auto_enabled }))}
                className={`w-12 h-6 rounded-full transition-all ${shoutoutSettings.auto_enabled ? 'bg-green-500' : 'bg-gray-300'}`}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow transition-all ${shoutoutSettings.auto_enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="w-4 h-4 inline mr-1" /> Volunteer Hours Milestone
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={shoutoutSettings.auto_volunteer_hours}
                    onChange={(e) => setShoutoutSettings(prev => ({ ...prev, auto_volunteer_hours: parseInt(e.target.value) || 0 }))}
                    className="flex-1 px-4 py-2 border rounded-lg"
                    disabled={!shoutoutSettings.auto_enabled}
                  />
                  <span className="text-gray-500">hours</span>
                </div>
                <p className="text-xs text-gray-400 mt-1">Auto-shoutout when employee reaches this many volunteer hours</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Heart className="w-4 h-4 inline mr-1" /> Donation Amount Milestone
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">$</span>
                  <input
                    type="number"
                    value={shoutoutSettings.auto_donation_amount}
                    onChange={(e) => setShoutoutSettings(prev => ({ ...prev, auto_donation_amount: parseInt(e.target.value) || 0 }))}
                    className="flex-1 px-4 py-2 border rounded-lg"
                    disabled={!shoutoutSettings.auto_enabled}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">Auto-shoutout when employee donates this total amount</p>
              </div>
            </div>
            
            <div className="flex justify-end">
              <button
                onClick={handleSaveSettings}
                className="px-6 py-2 text-white rounded-lg flex items-center gap-2"
                style={{ background: COLORS.primary }}
              >
                <Check className="w-4 h-4" /> Save Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Shoutout Modal */}
      {showShoutoutModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Give a Shoutout</h3>
                <p className="text-sm text-gray-500">Recognize employee contributions</p>
              </div>
              <button onClick={() => { setShowShoutoutModal(false); setSelectedEmployees([]); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Employee Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Employee(s) *</label>
                <div className="border rounded-lg max-h-40 overflow-y-auto">
                  {employees.map(emp => (
                    <label key={emp.user_id} className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b last:border-0">
                      <input
                        type="checkbox"
                        checked={selectedEmployees.includes(emp.user_id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedEmployees(prev => [...prev, emp.user_id]);
                          } else {
                            setSelectedEmployees(prev => prev.filter(id => id !== emp.user_id));
                          }
                        }}
                        className="w-4 h-4 rounded"
                      />
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ background: COLORS.primary }}>
                        {(emp.name || emp.email)?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{emp.name || 'Unnamed'}</p>
                        <p className="text-xs text-gray-500">{emp.email}</p>
                      </div>
                    </label>
                  ))}
                </div>
                {selectedEmployees.length > 0 && (
                  <p className="text-sm text-green-600 mt-1">{selectedEmployees.length} employee(s) selected</p>
                )}
              </div>
              
              {/* Shoutout Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Recognition Type</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'volunteering', label: 'Volunteering', icon: '🤝' },
                    { id: 'donation', label: 'Donation', icon: '💝' },
                    { id: 'milestone', label: 'Milestone', icon: '⭐' }
                  ].map(type => (
                    <button
                      key={type.id}
                      onClick={() => setShoutoutForm(prev => ({ ...prev, type: type.id }))}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        shoutoutForm.type === type.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-2xl">{type.icon}</span>
                      <p className="text-sm font-medium mt-1">{type.label}</p>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                <textarea
                  value={shoutoutForm.message}
                  onChange={(e) => setShoutoutForm(prev => ({ ...prev, message: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg resize-none"
                  rows={3}
                  placeholder="e.g., Amazing work volunteering 50 hours this quarter! Your dedication to our community is inspiring."
                />
              </div>
              
              {/* Public Toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Make Public</p>
                  <p className="text-xs text-gray-500">Show on Impact Portal's Wall of Fame</p>
                </div>
                <button
                  onClick={() => setShoutoutForm(prev => ({ ...prev, is_public: !prev.is_public }))}
                  className={`w-12 h-6 rounded-full transition-all ${shoutoutForm.is_public ? 'bg-green-500' : 'bg-gray-300'}`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-all ${shoutoutForm.is_public ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </div>
            
            <div className="p-6 border-t flex justify-end gap-3">
              <button onClick={() => { setShowShoutoutModal(false); setSelectedEmployees([]); }} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={handleCreateShoutout}
                disabled={submitting}
                className="px-6 py-2 text-white rounded-lg flex items-center gap-2"
                style={{ background: COLORS.primary }}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send Shoutout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GamificationView;
